import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { pipe, propEq, map, values, filter, anyPass } from 'ramda'
import { useSelector } from 'react-redux'
import { select } from '@/store'
import { Button } from '@/components/button'
import { createModal, useModalize } from '@/components/modalize/mui'
import { colors } from '@/styles'
import CautionIcon from '../modal/caution-icon.svg'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fcf5d8;
  height: 112px;
  width: 100%;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 24px;
`
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  button {
    width: 50%;
    :last-child {
      margin-left: 5px;
    }
  }
`

const Title = styled.h5`
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
`

const Paragraph = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  margin: 24px 0;
  text-align: left;
`
const MissingUserTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  margin: 24px 0 0 0;
  text-align: left;
`

const MissingUserText = styled.li`
  list-style: inside;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  text-align: left;
  margin-left: 8px;
`

const IncompleteModal = ({ contractId, usersWithMissingItems = [], handleConfirm }) => {
  const { close } = useModalize()
  const { t } = useTranslation()

  const { recipients = {} } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const recipientsWithMissingItems = pipe(
    values,
    filter(
      anyPass(
        map(user => {
          return propEq('recipient_id', user)
        }, usersWithMissingItems)
      )
    )
  )(recipients)

  const handleCancel = () => {
    close()
  }

  const handleSubmit = async () => {
    handleConfirm()
    close()
  }

  return (
    <Container>
      <IconWrapper>{<img src={CautionIcon} alt="icon" />}</IconWrapper>
      <TextWrapper>
        <Title>
          {t('l.incomplete_modal.missing_fields.title', 'Missing fields for recipients')}
        </Title>
        <ContentWrapper>
          <MissingUserTitle>
            {t('l.incomplete_modal.add_signature.text', 'Add signature or initial fields for:')}
          </MissingUserTitle>
          {recipientsWithMissingItems.map((user, index) => (
            <MissingUserText key={index}>{`${user.first_name} ${user.last_name}`}</MissingUserText>
          ))}
          <Paragraph>
            {t(
              'l.incomplete_modal.content.text',
              'If you send without fields, they will still receive a copy of the contract and are required to confirm the agreement terms.'
            )}
          </Paragraph>
        </ContentWrapper>
        <ButtonWrapper>
          <Button theme="secondary" onClick={handleCancel}>
            {t('l.incomplete_modal.dismiss.text', 'Dismiss')}
          </Button>
          <Button theme="primary" onClick={handleSubmit}>
            {t('l.incomplete_modal.continue_without_fields.text', 'Continue without fields')}
          </Button>
        </ButtonWrapper>
      </TextWrapper>
    </Container>
  )
}

const showIncompleteModal = createModal(IncompleteModal, { showClose: false, width: '498px' })

export default showIncompleteModal
